import { UserRole } from "@/shared/constants/user-roles"

export const COMPLETABLE_WORK_SCOPE_STATUSES = ["Complete", "Materials Only Complete"] as const

export const WORK_SCOPE_COMPLETE_PHOTO_WARNING = "Workscope Complete Photo not tagged"

export function workScopePhotoReplaceMessage(slot: "before" | "after"): string {
  return slot === "before"
    ? "This replaces the current before photo"
    : "This replaces the current after photo"
}

export const WORK_SCOPE_COMPLETE_ALREADY_TAGGED_MESSAGE =
  "This photo is already tagged to another work scope. Untag it before tagging a different scope."

const TAG_ROLES = new Set<string>([UserRole.ADMIN, UserRole.BASE_LEADER, UserRole.TEAM_LEADER])
const REMOVE_ON_HOME_ROLES = new Set<string>([UserRole.ADMIN, UserRole.BASE_LEADER])

export function canSeeWorkScopeCompleteTagControl(role?: string | null): boolean {
  return !!role && TAG_ROLES.has(role)
}

export function isPhotoEligibleForWorkScopeCompleteTag(photo: {
  mediaType?: string | null
  tags?: string[] | null
}): boolean {
  if (photo.mediaType !== "image") return false
  return !(photo.tags ?? []).includes("Confidential")
}

export function canUntagWorkScopeComplete(input: {
  role?: string | null
  userId?: number | null
  taggedByUserId?: number | null
}): boolean {
  if (!input.role) return false
  if (input.role === UserRole.ADMIN || input.role === UserRole.BASE_LEADER) return true
  return input.taggedByUserId != null && input.taggedByUserId === input.userId
}

export function canRemoveWorkScopeCompleteOnHomeTab(role?: string | null): boolean {
  return !!role && REMOVE_ON_HOME_ROLES.has(role)
}

export function isCompletableWorkScopeStatus(status: string | null | undefined): boolean {
  return status === "Complete" || status === "Materials Only Complete"
}

/** UTC calendar date from an ISO timestamp, without shifting to the local timezone. */
export function utcDatePart(value: string): string | null {
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/)
  if (match) return match[1]
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toISOString().slice(0, 10)
}

export function localCalendarDate(now = new Date()): string {
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, "0")
  const day = String(now.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

export function completionDateFromTakenAt(
  takenAt: string | null | undefined,
  today: string,
): string {
  if (takenAt) {
    const utcDate = utcDatePart(takenAt)
    if (utcDate) return utcDate
  }
  return today
}

export type WorkScopeStatusChangeDecision =
  | { allow: true; nextDate?: string | null }
  | { allow: false; warning: string }

/**
 * Decides a user-driven status dropdown change.
 * Callers must not use this on form load, reset, or Save Data.
 */
export function decideWorkScopeStatusChange(input: {
  nextStatus: string
  currentDate: string | null | undefined
  takenAt: string | null | undefined
  today: string
}): WorkScopeStatusChangeDecision {
  if (!isCompletableWorkScopeStatus(input.nextStatus)) {
    if (input.currentDate) {
      return { allow: true, nextDate: null }
    }
    return { allow: true }
  }

  const dateEmpty = input.currentDate == null || input.currentDate === ""
  if (dateEmpty) {
    return {
      allow: true,
      nextDate: completionDateFromTakenAt(input.takenAt, input.today),
    }
  }

  return { allow: true }
}
